import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { navigate } from 'gatsby'
import { store } from '@redux/store'
import { CircularProgress, styled } from '@mui/material'
import {
  applePayEnabled,
  applePaySupported,
  onlyNumbers,
  scrollTo,
  getApplePayTotal,
  getApplePayCartTotal,
  getApplePayTotalV2,
  getApplePayCartTotalV2,
} from '@helpers/checkout/payment-section/apple-pay'
import { addToCart } from '@helpers/cart'
import { createOrder, updateLineItems, updateAddress, updateDelivery, updatePayment } from '@services/checkout'
import { setOrder, setCheckoutStep, setDeliveryMode } from '@redux/modules/checkout'
import { addToDataLayer, checkoutStepAnalytics } from '@helpers/google-tag-manager'
import { getAddressSpecificBody, checkShippingZip } from '@helpers/checkout/shipping-section'
import { getDeliverySpecificBody, setDeliveryCalendar } from '@helpers/checkout/delivery-section'
import { getOrder, getLineItems } from '@helpers/checkout/global'
import { getCurrentLocation } from '@helpers/geo-location'
import { getFromBrowserStorage } from '@helpers/storage'
import { setCheckoutReviewStep } from '@helpers/checkout/local-storage'
import { ApplePayButton as ApplePay } from '@rtgdev/design-system'
import useCartVersionChecker from '@hooks/useCartVersionChecker'
import '@assets/css/components/shared/apple-pay.sass'

const ApplePayLoadingWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '2.25em',
  width: '100%',
})

const ApplePayButton = (
  { activeAddons, componentPage, isExpress, isPDP, price, product, type, warrantyEnabled, cartV2Data },
  ref,
) => {
  const [clicked, setClicked] = useState(false)
  const [showApplePay, setShowApplePay] = useState(false)
  const [orderTotal, setOrderTotal] = useState(0)
  const [cartTotal, setCartTotal] = useState(0)
  const cartVersion = useCartVersionChecker()
  const isCartV2 = cartVersion === 'v2'

  useEffect(() => {
    if (isCartV2 && cartV2Data) {
      // Use cart v2 helper functions when cart v2 data is available
      setOrderTotal(getApplePayTotalV2(cartV2Data))
      setCartTotal(getApplePayCartTotalV2(cartV2Data))
    } else {
      // Fall back to cart v1 helper functions
      setOrderTotal(getApplePayTotal())
      setCartTotal(getApplePayCartTotal())
    }
    setShowApplePay(applePaySupported() && applePayEnabled())
  }, [isCartV2, cartV2Data])

  useImperativeHandle(ref, () => ({
    handleClick,
  }))

  const checkOrderId = async () => {
    let order = getFromBrowserStorage('session', 'order')
    if (!order.orderId || order.orderId === undefined) {
      const location = getCurrentLocation()
      const lineItems = getLineItems()
      await createOrder({
        lineItems,
        region: location.region,
        zone: parseInt(location.price_zone),
        distribution_index: parseInt(location.distribution_index),
      })
        .then(response => response)
        .then(newOrder => {
          store.dispatch(setOrder(newOrder))
          order = newOrder
        })
    }
    return order
  }

  const createCartv2Order = async () => {
    try {
      const orderResp = await createOrder({ eCartId: cartV2Data?._id })
      return orderResp
    } catch (error) {
      console.error('Error creating order:', error)
      return null
    }
  }

  const updateCartItems = async order => {
    const lineItems = getLineItems()
    const storeCartLineItems = order.lineItems.filter(lineItem => lineItem.isStoreSku)
    const updateOrder = await updateLineItems({
      orderId: order.orderId,
      lineItems: [...lineItems, ...storeCartLineItems],
    })
    store.dispatch(setOrder(updateOrder))
  }

  const addToCartPdp = () => {
    addToDataLayer('click', componentPage, 'add to cart', product.sku)
    addToCart({ product, price, activeAddons, warrantyEnabled })
    const viewCartModal = document.getElementsByClassName('view-cart')[0]
    if (viewCartModal) {
      viewCartModal.focus()
    }
  }

  const handleClick = () => {
    setClicked(true)
    const order = getOrder()
    let hasStoreCart = false
    if (isCartV2 && cartV2Data) {
      hasStoreCart = cartV2Data?.lineItems?.some(item => item.isStoreSku) ?? false
    } else {
      hasStoreCart = order?.lineItems?.some(item => item.isStoreSku) ?? false
    }

    if (isPDP) {
      addToCartPdp()
      createPaySession(order, hasStoreCart)
    } else {
      createPaySession(order, hasStoreCart)
    }
  }

  const createPaySession = (pOrder, storeCart) => {
    const rtgOrder = `Roomstogo Furniture Order${isPDP || isExpress ? ' Subtotal' : ''}`
    let order = { ...pOrder }
    let remainingBalance

    // Use cart v2 totals if available, otherwise fall back to cart v1
    if (isCartV2 && cartV2Data) {
      remainingBalance = onlyNumbers(getApplePayTotalV2(cartV2Data) || orderTotal || order.total)

      if (remainingBalance === 0 || isPDP) {
        remainingBalance = onlyNumbers(getApplePayCartTotalV2(cartV2Data) || cartTotal || order.total)
      }
    } else {
      remainingBalance = onlyNumbers(getApplePayTotal() || orderTotal || order.total)

      if (remainingBalance === 0 || isPDP) {
        remainingBalance = onlyNumbers(getApplePayCartTotal() || cartTotal || order.total)
      }
    }

    const paymentRequest = {
      countryCode: 'US',
      currencyCode: 'USD',
      supportedNetworks: ['visa', 'masterCard', 'amex'],
      merchantCapabilities: ['supports3DS'],
      total: { label: rtgOrder, amount: remainingBalance },
      requiredBillingContactFields: ['postalAddress'],
    }

    if (!storeCart && isExpress) {
      paymentRequest.requiredShippingContactFields = ['postalAddress', 'email', 'phone'] // if pdp, fullCart page
    }

    const session = new window.ApplePaySession(1, paymentRequest)

    session.onvalidatemerchant = event => {
      const { validationURL } = event
      addToDataLayer('click', 'cart', 'apple pay')

      const merchantSessionUrl = process.env.GATSBY_APPLEPAY_URL.endsWith('/')
        ? `${process.env.GATSBY_APPLEPAY_URL}merchantSession`
        : `${process.env.GATSBY_APPLEPAY_URL}/merchantSession`

      fetch(merchantSessionUrl, {
        body: JSON.stringify({
          url: validationURL,
          initiativeContext: window.location.hostname,
          storeCart,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        method: 'POST',
      })
        .then(response => response.json())
        .then(async json => {
          session.completeMerchantValidation(json)
          if (isPDP) {
            order = await checkOrderId() // check if orderId is available or createOrder
            updateCartItems(order, storeCart) // update order cart line items
          }
          if (isExpress && isCartV2 && !order?.eCart?.id) {
            order = await createCartv2Order()
          }
        })
        .catch(error => {
          // eslint-disable-next-line no-alert
          alert("We're unable to process this payment, please try again.")
          setClicked(false)
          throw new Error(error)
        })
    }

    session.onpaymentauthorized = async event => {
      const { payment } = event

      const sc = payment.shippingContact
      const bc = payment.billingContact
      const tok = payment.token

      order.selectedPaymentType = 'Apple Pay'
      order.payer = {
        ...order.payer,
        firstName: bc.givenName,
        lastName: bc.familyName,
        email: !storeCart && isExpress ? sc.emailAddress : order.contact.email,
        phone: !storeCart && isExpress ? sc.phoneNumber : order.contact.phone,
      }
      order.billingAddress = {
        ...order.billingAddress,
        address1: bc.addressLines[0],
        address2: bc.addressLines[1] ? bc.addressLines[1] : '',
        city: bc.locality,
        state: bc.administrativeArea,
        zip: bc.postalCode,
      }

      if (!storeCart && isExpress) {
        order.contact = {
          ...order.contact,
          firstName: sc.givenName,
          lastName: sc.familyName,
          email: sc.emailAddress,
          phone: sc.phoneNumber,
        }
        order.shippingAddress = {
          ...order.shippingAddress,
          address1: sc.addressLines[0],
          address2: sc.addressLines[1] ? bc.addressLines[1] : '',
          city: sc.locality,
          state: sc.administrativeArea,
          zip: sc.postalCode,
          showAddressLookup: false,
        }
      }

      store.dispatch(setOrder(order))

      if (isExpress) {
        let validState = true
        validState = await checkShippingZip(!storeCart && isExpress ? sc.postalCode : order.shippingAddress.zip).catch(
          () => {
            validState = false
          },
        )
        if (validState) {
          const addrOrder = await updateAddress(getAddressSpecificBody(order, true)).catch(err => {
            setClicked(false)
            throw new Error(err)
          })
          if (addrOrder?.deliveryCalendar.length > 0) {
            const calendar = setDeliveryCalendar(
              addrOrder.deliveryCalendar,
              addrOrder.pickupCalendar,
              addrOrder.expressCalendar,
              false,
            )
            addrOrder.deliveryDate =
              calendar.filter(date => date.isStandardDelivery && !date.isExpressDelivery)[0].date || calendar[0].date
          }
          updateDelivery(getDeliverySpecificBody(addrOrder))
            .then(() => {
              store.dispatch(setDeliveryMode('delivery'))
              store.dispatch(setCheckoutStep('review'))
              updatePayment({
                paymentInfo: [
                  {
                    paymentType: 'APP',
                    paymentProperties: {
                      paymentData: tok.paymentData,
                      paymentMethod: tok.paymentMethod,
                      transactionIdentifier: tok.transactionIdentifier,
                    },
                  },
                ],
                orderId: order.orderId,
              })
                .then(paidOrder => {
                  session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
                  store.dispatch(setOrder(paidOrder))
                  setCheckoutReviewStep()
                  checkoutStepAnalytics('review')
                  setTimeout(() => {
                    // TODO: navigation need to add code to track ga data
                    navigate('/checkout')
                  }, 250)
                  scrollTo('payment')
                })
                .catch(error => {
                  session.completePayment(window.ApplePaySession.STATUS_FAILURE)
                  // eslint-disable-next-line no-alert
                  alert("We're unable to process this payment, please try again.")
                  setClicked(false)
                })
            })
            .catch(error => {
              throw new Error('Error with Apple Pay:', error)
            })
        } else if (window) {
          window.location.reload()
        }
      } else {
        updatePayment({
          paymentInfo: [
            {
              paymentType: 'APP',
              paymentProperties: {
                paymentData: tok.paymentData,
                paymentMethod: tok.paymentMethod,
                transactionIdentifier: tok.transactionIdentifier,
              },
            },
          ],
          orderId: order.orderId,
        })
          .then(paidOrder => {
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
            setClicked(false)
            store.dispatch(setOrder(paidOrder))
            checkoutStepAnalytics('review')
            scrollTo('payment')

            updateAddress(getAddressSpecificBody(paidOrder, true)).catch(() => {
              throw new Error('Error updating address')
            })
          })
          .catch(error => {
            session.completePayment(window.ApplePaySession.STATUS_FAILURE)
            // eslint-disable-next-line no-alert
            alert("We're unable to process this payment, please try again.")
            setClicked(false)
            throw new Error('Error with Apple Pay:', error)
          })
      }
    }

    // eslint-disable-next-line no-unused-vars
    session.oncancel = event => {
      // alert("We're unable to proccess this payment, please try again.")
      setClicked(false)
    }

    session.begin()
  }

  return (
    <>
      {showApplePay && !clicked && (
        <ApplePay
          buttonStyle="black"
          type={type.toLowerCase()}
          onClick={() => handleClick()}
          onKeyDown={() => handleClick()}
        />
      )}
      {clicked && (
        <ApplePayLoadingWrapper className="applepay-button-wrapper" alt="" role="presentation">
          <CircularProgress size="1.5rem" aria-label="Processing Apple Pay payment" aria-busy="true" />
        </ApplePayLoadingWrapper>
      )}
    </>
  )
}

ApplePayButton.propTypes = {
  activeAddons: PropTypes.any,
  componentPage: PropTypes.any,
  isExpress: PropTypes.any,
  isPDP: PropTypes.any,
  price: PropTypes.any,
  product: PropTypes.shape({
    sku: PropTypes.any,
  }),
  type: PropTypes.any,
  warrantyEnabled: PropTypes.any,
  cartV2Data: PropTypes.any,
}

export default forwardRef(ApplePayButton)
